import { Badge } from '../../components/ui/badge';
import {
  CalendarClock, Play, CheckCircle2, XCircle, AlertTriangle, Circle, MinusCircle,
  ShieldCheck, ShieldAlert, ShieldQuestion, type LucideIcon,
} from 'lucide-react';
import type { ActionPriority, ActionStatus, AuditOutcome, AuditStatus } from '../../lib/api/audits';

interface BadgeStyle {
  icon: LucideIcon;
  className: string;
  label: string;
}

const GOOD = 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900';
const DUE = 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900';
const LATE = 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900';
const NEUTRAL = 'bg-muted text-muted-foreground';

export const AUDIT_STATUS: Record<AuditStatus, BadgeStyle> = {
  planned: { icon: CalendarClock, className: DUE, label: 'Planned' },
  in_progress: { icon: Play, className: DUE, label: 'In progress' },
  completed: { icon: CheckCircle2, className: GOOD, label: 'Completed' },
  cancelled: { icon: MinusCircle, className: NEUTRAL, label: 'Cancelled' },
};

export const AUDIT_OUTCOME: Record<Exclude<AuditOutcome, ''>, BadgeStyle> = {
  pass: { icon: ShieldCheck, className: GOOD, label: 'Pass' },
  conditional: { icon: ShieldQuestion, className: DUE, label: 'Conditional pass' },
  fail: { icon: ShieldAlert, className: LATE, label: 'Fail' },
};

export const ACTION_STATUS: Record<ActionStatus, BadgeStyle> = {
  open: { icon: Circle, className: DUE, label: 'Open' },
  in_progress: { icon: Play, className: DUE, label: 'In progress' },
  overdue: { icon: AlertTriangle, className: LATE, label: 'Overdue' },
  completed: { icon: CheckCircle2, className: GOOD, label: 'Completed' },
  cancelled: { icon: XCircle, className: NEUTRAL, label: 'Cancelled' },
};

export const ACTION_PRIORITY: Record<ActionPriority, BadgeStyle> = {
  low: { icon: Circle, className: NEUTRAL, label: 'Low' },
  medium: { icon: Circle, className: DUE, label: 'Medium' },
  high: { icon: AlertTriangle, className: LATE, label: 'High' },
};

function StyledBadge({ style, testId }: { style: BadgeStyle; testId?: string }) {
  const Icon = style.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${style.className}`} data-testid={testId}>
      <Icon className="h-3 w-3" />
      {style.label}
    </Badge>
  );
}

export function AuditStatusBadge({ status, overdue }: { status: AuditStatus; overdue?: boolean }) {
  if (overdue && (status === 'planned' || status === 'in_progress')) {
    return <StyledBadge style={{ icon: AlertTriangle, className: LATE, label: status === 'planned' ? 'Overdue' : 'In progress · overdue' }} testId="audit-status" />;
  }
  return <StyledBadge style={AUDIT_STATUS[status]} testId="audit-status" />;
}

export function AuditOutcomeBadge({ outcome }: { outcome: AuditOutcome }) {
  if (!outcome) return <span className="text-sm text-muted-foreground">—</span>;
  return <StyledBadge style={AUDIT_OUTCOME[outcome]} testId="audit-outcome" />;
}

export function ActionStatusBadge({ status }: { status: ActionStatus }) {
  return <StyledBadge style={ACTION_STATUS[status]} testId="action-status" />;
}

export function ActionPriorityBadge({ priority }: { priority: ActionPriority }) {
  return <StyledBadge style={ACTION_PRIORITY[priority]} testId="action-priority" />;
}

export const AUDIT_TYPE_LABELS: Record<string, string> = {
  site: 'Site audit',
  quality: 'Quality audit',
  process: 'Process audit',
  hse: 'Health & safety audit',
  compliance: 'Compliance review',
  other: 'Other',
};
